import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaEdit, FaCheck, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import useColleges from '../../../../utils/useColleges';
import AttendanceNav from '../AttendanceNav';
import '../AttendanceNav.css';
import './AttendanceList.css';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' },   { value: 4, label: 'April' },
  { value: 5, label: 'May' },     { value: 6, label: 'June' },
  { value: 7, label: 'July' },    { value: 8, label: 'August' },
  { value: 9, label: 'September' },{ value: 10, label: 'October' },
  { value: 11, label: 'November' },{ value: 12, label: 'December' },
];

const currentYear  = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const AttendanceList = () => {
  const { user }    = useAuth();
  const isAdmin     = user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading }    = useColleges();

  const [selectedCollege, setSelectedCollege] = useState('');
  const [selectedYear, setSelectedYear]       = useState(currentYear);
  const [selectedMonth, setSelectedMonth]     = useState(currentMonth);
  const [selectedDept, setSelectedDept]       = useState('');
  const [deptSearchInput, setDeptSearchInput] = useState('All Departments');
  const [showDeptDrop, setShowDeptDrop]       = useState(false);
  const [departments, setDepartments]         = useState([]);
  const [records, setRecords]                 = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [expandedRow, setExpandedRow]         = useState(null);
  
  const [editing, setEditing]                 = useState({});
  const [savingId, setSavingId]               = useState(null);
  const [paidEmpIds, setPaidEmpIds]           = useState(new Set());

  const deptDropRef = useRef(null);

  useEffect(() => {
    if (!isAdmin && colleges.length > 0) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);
  useEffect(() => {
    if (isAdmin && colleges.length === 1) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);

  useEffect(() => {
    if (!selectedCollege) { setDepartments([]); return; }
    api.get('/departments', { params: { college: selectedCollege } })
      .then(r => setDepartments(r.data.data || [])).catch(() => {});
  }, [selectedCollege]);

  useEffect(() => {
    if (!selectedCollege) { setRecords([]); return; }
    setLoading(true);
    api.get('/attendance', { params: { college: selectedCollege, month: selectedMonth, year: selectedYear } })
      .then(r => setRecords(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    // fetch paid salaries for this month to lock edit/delete
    api.get('/salary', { params: { college: selectedCollege, month: selectedMonth, year: selectedYear } })
      .then(r => {
        const ids = new Set(
          (r.data.data || []).filter(s => s.isPaid).map(s => String(s.employee?._id || s.employee))
        );
        setPaidEmpIds(ids);
      })
      .catch(() => setPaidEmpIds(new Set()));
  }, [selectedCollege, selectedMonth, selectedYear]);

  useEffect(() => {
    const handler = (e) => {
      if (deptDropRef.current && !deptDropRef.current.contains(e.target)) {
        setShowDeptDrop(false);
        setDeptSearchInput(
          selectedDept ? departments.find(d => d._id === selectedDept)?.name || 'All Departments' : 'All Departments'
        );
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedDept, departments]);

  const filteredDepts = useMemo(() => {
    const q = deptSearchInput.toLowerCase();
    if (q === 'all departments' || q === '') return departments;
    return departments.filter(d => d.name.toLowerCase().includes(q));
  }, [deptSearchInput, departments]);

  const displayedRecords = useMemo(() => {
    let res = records;
    if (selectedDept) {
      res = res.filter(r => String(r.employee?.department?._id || r.employee?.department) === selectedDept);
    }
    return res;
  }, [records, selectedDept]);

  const handleEditChange = (id, value, max) => {
    const num = parseFloat(value) || 0;
    const clamped = Math.min(Math.max(0, num), max);
    setEditing(prev => ({ ...prev, [id]: clamped }));
  };

  const handleEditClick = async (record) => {
    // Check if holidays have changed since this record was saved
    const startDate = new Date(record.year, record.month - 1, 1);
    const endDate = new Date(record.year, record.month, 0, 23, 59, 59, 999);
    
    try {
      const response = await api.get('/holidays');
      const currentHolidays = (response.data.data || []).filter(h => {
        const hDate = new Date(h.date);
        return String(h.college?._id || h.college) === String(selectedCollege) &&
          hDate >= startDate && hDate <= endDate;
      });
      
      const currentHolidayCount = currentHolidays.length;
      const savedHolidayCount = record.holidays;
      
      if (currentHolidayCount !== savedHolidayCount) {
        const change = currentHolidayCount > savedHolidayCount ? 'added' : 'deleted';
        const newTotal = Math.min(record.presentDays + currentHolidayCount, record.daysInMonth);
        
        // Show alert with OK/Cancel
        const result = await Swal.fire({
          title: 'Holiday Count Changed!',
          html: `
            <div style="text-align: left; padding: 10px;">
              <p><strong>Holidays:</strong> ${savedHolidayCount} → ${currentHolidayCount} (${change})</p>
              <p><strong>Present Days:</strong> ${record.presentDays}</p>
              <p><strong>Old Total:</strong> ${record.totalPresent}</p>
              <p><strong>New Total:</strong> ${newTotal}</p>
              <hr style="margin: 10px 0;">
              <p style="color: #f59e0b;">⚠️ Do you want to update this record with the new holiday count?</p>
            </div>
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#10b981',
          cancelButtonColor: '#94a3b8',
          confirmButtonText: 'Yes, Update It!',
          cancelButtonText: 'Cancel'
        });
        
        if (result.isConfirmed) {
          // User clicked OK - Auto-update the record
          try {
            // amazonq-ignore-next-line
            const { data } = await api.put(`/attendance/${record._id}`, { presentDays: record.presentDays });
            setRecords(prev => prev.map(r => r._id === record._id ? data.data : r));
            Swal.fire('Updated!', 'Attendance updated with new holiday count.', 'success');
          } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Failed to update.', 'error');
          }
        }
        // If cancelled, do nothing
      } else {
        // No holiday change, just enter edit mode
        setEditing(prev => ({ ...prev, [record._id]: record.presentDays }));
      }
    } catch (err) {
      // If error fetching holidays, just allow editing
      setEditing(prev => ({ ...prev, [record._id]: record.presentDays }));
    }
  };

  const handleSaveEdit = async (record) => {
    const newPresent = editing[record._id];
    if (newPresent === undefined) return;
    if (Number(newPresent) === Number(record.presentDays)) {
      setEditing(prev => { const n = { ...prev }; delete n[record._id]; return n; });
      return;
    }
    setSavingId(record._id);
    try {
      // amazonq-ignore-next-line
      const { data } = await api.put(`/attendance/${record._id}`, { presentDays: newPresent });
      setRecords(prev => prev.map(r => r._id === record._id ? data.data : r));
      setEditing(prev => { const n = { ...prev }; delete n[record._id]; return n; });
      Swal.fire('Updated!', 'Attendance updated.', 'success');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to update.', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will remove the attendance record for this month.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/attendance/${id}`);
        setRecords(prev => prev.filter(r => r._id !== id));
        Swal.fire('Deleted!', 'Attendance record removed.', 'success');
      } catch (err) {
        Swal.fire('Error', 'Failed to delete attendance.', 'error');
      }
    }
  };

  return (
    <div className="attendance-list-of-employee-container">
      <h2 className="attendance-list-of-employee-title">Attendance List</h2>
      <AttendanceNav />

      {/* Filters */}
      <div className="attendance-list-of-employee-filters-wrapper">
        {isAdmin && (
          <div className="attendance-list-of-employee-filter-group">
            <label className="attendance-list-of-employee-filter-label">College</label>
            <select className="attendance-list-of-employee-select-input"
              value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)}>
              <option value="">-- Select College --</option>
              {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        )}

        <div className="attendance-list-of-employee-filter-group">
          <label className="attendance-list-of-employee-filter-label">Year</label>
          <select className="attendance-list-of-employee-select-input"
            value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
            {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map(y =>
              <option key={y} value={y}>{y}</option>
            )}
          </select>
        </div>

        <div className="attendance-list-of-employee-filter-group">
          <label className="attendance-list-of-employee-filter-label">Month</label>
          <select className="attendance-list-of-employee-select-input"
            value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        <div className="attendance-list-of-employee-filter-group" ref={deptDropRef}>
          <label className="attendance-list-of-employee-filter-label">Department</label>
          <div className="attendance-list-of-employee-searchable-dropdown">
            <FaSearch className="attendance-list-of-employee-search-icon" />
            <input type="text" className="attendance-list-of-employee-dept-input"
              value={deptSearchInput}
              onChange={e => { setDeptSearchInput(e.target.value); setShowDeptDrop(true); }}
              onClick={() => { setDeptSearchInput(''); setShowDeptDrop(true); }}
              placeholder="Search department..."
            />
            <FaChevronDown className="attendance-list-of-employee-chevron-icon" />
            {showDeptDrop && (
              <div className="attendance-list-of-employee-dropdown-list">
                <ul>
                  <li onClick={() => { setSelectedDept(''); setDeptSearchInput('All Departments'); setShowDeptDrop(false); }}>All Departments</li>
                  {filteredDepts.map(d => (
                    <li key={d._id} onClick={() => { setSelectedDept(d._id); setDeptSearchInput(d.name); setShowDeptDrop(false); }}>{d.name}</li>
                  ))}
                  {filteredDepts.length === 0 && <li className="attendance-list-of-employee-dropdown-empty">No departments found</li>}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="attendance-list-of-employee-table-wrapper">
        <table className="attendance-list-of-employee-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th className="attendance-list-of-employee-align-left">Employee Name</th>
              <th className="attendance-list-col-dept">Department</th>
              <th className="attendance-list-col-days">Total Days</th>
              <th className="attendance-list-col-holiday">Holidays</th>
              <th>Present Days</th>
              <th className="attendance-list-col-total">Total Present</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="attendance-list-of-employee-empty-msg">Loading...</td></tr>
            ) : !selectedCollege ? (
              <tr><td colSpan="8" className="attendance-list-of-employee-empty-msg">Please select a college.</td></tr>
            ) : displayedRecords.length === 0 ? (
              <tr><td colSpan="8" className="attendance-list-of-employee-empty-msg">
                No attendance records found for {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}.
              </td></tr>
            ) : displayedRecords.map((record, i) => {
              const isEditing  = editing[record._id] !== undefined;
              const maxPresent = record.daysInMonth - record.holidays;
              const isPaid     = paidEmpIds.has(String(record.employee?._id || record.employee));
              return (
                <React.Fragment key={record._id}>
                  <tr className="attendance-list-of-employee-main-row">
                    <td>{i + 1}</td>
                    <td className="attendance-list-of-employee-align-left">
                      <div className="attendance-list-of-employee-name-wrapper"
                        onClick={() => setExpandedRow(expandedRow === record._id ? null : record._id)}>
                        <span>{record.employee?.name || '—'}</span>
                        <button className="attendance-list-of-employee-expand-btn">
                          {expandedRow === record._id ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>
                    <td className="attendance-list-col-dept">
                      <span className="attendance-list-of-employee-badge-dept">
                        {record.employee?.department?.name || '—'}
                      </span>
                    </td>
                    <td className="attendance-list-col-days">{record.daysInMonth}</td>
                    <td className="attendance-list-col-holiday">{record.holidays}</td>
                    <td>
                      {isEditing ? (
                        <input type="number" className="att-list-edit-input"
                          min="0" max={maxPresent}
                          value={editing[record._id]}
                          onChange={e => handleEditChange(record._id, e.target.value, maxPresent)}
                        />
                      ) : (
                        <span className="att-list-present-val">{record.presentDays}</span>
                      )}
                    </td>
                    <td className="attendance-list-col-total">
                      <span className="attendance-list-of-employee-badge-total">
                        {record.totalPresent}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        {isPaid ? (
                          <span className="att-list-paid-badge">✓ Salary Paid</span>
                        ) : isEditing ? (
                          <>
                            <button className="att-list-save-btn" disabled={savingId === record._id}
                              onClick={() => handleSaveEdit(record)}>
                              <FaCheck /> {savingId === record._id ? '...' : 'Save'}
                            </button>
                            <button className="att-list-cancel-btn"
                              onClick={() => setEditing(prev => { const n = { ...prev }; delete n[record._id]; return n; })}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="att-list-edit-btn"
                              onClick={() => handleEditClick(record)}>
                              <FaEdit />
                            </button>
                            <button className="att-list-delete-btn" onClick={() => handleDelete(record._id)}>
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedRow === record._id && (
                    <tr className="attendance-list-of-employee-expanded-row">
                      <td colSpan="8">
                        <div className="attendance-list-of-employee-expanded-content">
                          <div className="attendance-list-of-employee-expanded-item attendance-list-exp-dept">
                            <span className="attendance-list-of-employee-exp-label">Department</span>
                            <span className="attendance-list-of-employee-exp-value">{record.employee?.department?.name || '—'}</span>
                          </div>
                          <div className="attendance-list-of-employee-expanded-item attendance-list-exp-days">
                            <span className="attendance-list-of-employee-exp-label">Total Days</span>
                            <span className="attendance-list-of-employee-exp-value">{record.daysInMonth}</span>
                          </div>
                          <div className="attendance-list-of-employee-expanded-item attendance-list-exp-holiday">
                            <span className="attendance-list-of-employee-exp-label">Holidays</span>
                            <span className="attendance-list-of-employee-exp-value">{record.holidays}</span>
                          </div>
                          <div className="attendance-list-of-employee-expanded-item attendance-list-exp-total">
                            <span className="attendance-list-of-employee-exp-label">Total Present</span>
                            <span className="attendance-list-of-employee-exp-value">{record.totalPresent}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceList;